// Named `home.tsx` instead of `index.tsx` to avoid having a 1-segment dynamic page (`/[website]`)
// in the pages list. The Next.js client-side prefetcher otherwise pattern-matches Link `as` like
// `/<slug>` against `[website]/index` and overwrites `query.website` with the slug
// (cf. node_modules/next/dist/shared/lib/router/router.js:1389 — `Object.assign(query, getRouteMatcher(...))`),
// producing a 403 on multi-tenant deployments. Middleware rewrites `/` → `/home` for the user.
import omitBy from 'lodash/omitBy';
import type { GetStaticPaths } from 'next';
import { useEffect } from 'react';
import { defineMessages, useIntl } from 'react-intl';

import { optiDigitalCancelInfiniteLoad } from '../../../../front/src/components/Ads/OptiDigitalAds';
import { insertLayoutData } from '../../../../front/src/contributor/api/layout';
import { Block } from '../../../../front/src/contributor/Layout/Block';
import { useDataExposer } from '../../../../front/src/hooks/useExposedData';
import { useWebsite } from '../../../../front/src/hooks/useWebsite';
import { handleGqlError } from '../../../../front/src/services/graphql/gql-utils';
import { makeHomeTopicsVariables } from '../../../../front/src/thread/api/topic';
import type { QueryType } from '../../../../front/src/types/api';
import type { LayoutBlock, PageType } from '../../../../front/src/types/layout';
import { isBlankMeta, MetaPage } from '../../components/Meta/MetaPage';
import type { GetStaticPropsGD } from '../../utils/getStaticPropsCommon';
import {
  defaultStaticPathsNoPreRender,
  getStaticPropsCommon,
} from '../../utils/getStaticPropsCommon';
import { checkWebsiteIsVIP } from '../../utils/middleware-utils';

const pageName = 'homepage';

export const homepageMessages = defineMessages({
  homePageName: { defaultMessage: 'Homepage', id: 'rxNddi' },
});

export interface HomeProps {
  initialVariables: ReturnType<typeof makeHomeTopicsVariables>;
  websiteIsVIP: boolean;
  contentAreaLayout: LayoutBlock[];
  initialData?: QueryType;
}

export const Home: PageType<HomeProps> = props => {
  const { graphdebate } = useWebsite();
  const meta = graphdebate?.meta || {};
  const { formatMessage } = useIntl();
  const dataExposer = useDataExposer();

  const { contentAreaLayout } = props;

  useEffect(() => {
    optiDigitalCancelInfiniteLoad();
    dataExposer.setDataExposed({
      pageName: 'home',
    });
  }, [dataExposer]);

  const layoutContent = contentAreaLayout?.map(block => <Block key={block?._id} block={block} />);

  const title = `${formatMessage(homepageMessages.homePageName)} - {websiteName}`;

  return (
    <>
      {/* Fallback description home = nom du site (`{websiteName}`, toujours injecté par MetaPage),
          quand le template datalayer `meta.homepage` du tenant est vide (B8_04 objectif 4 ; aligné
          sur l'`og:description=name` de MetaWebsite). Spreadé AVANT le datalayer → un datalayer
          rempli garde la priorité. Le datalayer est nettoyé de ses valeurs vides (`isBlankMeta` :
          null/undefined/'') : non configuré, il vaut `{description:'',…}` et écraserait sinon. */}
      <MetaPage
        title={title}
        canonicalPath="/"
        description="{websiteName}"
        {...omitBy(meta?.homepage ?? {}, isBlankMeta)}
        index
        follow
      />
      <div className="Home flex flex-col md:flex-row gap-10">
        <div className="flex-1 flex flex-col space-y-5 max-w-full">
          {/* <LayoutContentToSpread /> */}
          {layoutContent}
        </div>
      </div>
    </>
  );
};

// See packages/graphdebate/src/pages/[website]/t/[slug].tsx for getStaticProps instructions
// export const getServerSideProps: GetServerSidePropsGD = async context => {
export const getStaticProps: GetStaticPropsGD<HomeProps> = async context => {
  // Common code
  let { response, gqlClient, website } = await getStaticPropsCommon(context, Home);

  if ('redirect' in response || 'notFound' in response) {
    return response;
  }

  const { customLayout, headings, ...otherProps } = response.props;

  const query = (context.params || {}) as any;
  const graphdebate = website.graphdebate!;
  const websiteIsVIP = checkWebsiteIsVIP(website);

  const variables = makeHomeTopicsVariables(graphdebate, query);

  const homeCustomLayout = [...customLayout];

  try {
    const layoutWithData = await insertLayoutData(gqlClient, homeCustomLayout, headings);

    response.props = {
      ...otherProps,
      customLayout: layoutWithData,
      headings,
      pageConfig: { previousPage: null },
      initialVariables: variables,
      websiteIsVIP,
    };

    return response;
  } catch (error: any) {
    handleGqlError(error);
    throw error;
  }
};

export const getStaticPaths: GetStaticPaths = defaultStaticPathsNoPreRender;

Home.pageName = () => pageName;

export default Home;
